/**
 * 健康度の説明モーダルコンポーネント
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business EVM健康度スコアの計算式・3軸評価・等級・データソースを説明するモーダル
 * @graph-connects Modal [calls] モーダルコンポーネント描画
 */

import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { HEALTH_COLORS } from './HealthBadge.js';
import { GRADE_THRESHOLDS } from '../../../types/project.js';

/** 健康度の説明モーダル */
/** @graph-connects none */
export function HealthInfoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} title={t('projects.health.modal.title')} size="full">
      <div className="space-y-6 text-sm text-[var(--color-text-secondary)] leading-relaxed">
        <p className="text-[var(--color-text-primary)]">{t('projects.health.modal.description')}</p>

        {/* 計算式 — 目立つデザイン */}
        <div className="rounded-xl border-2 border-[var(--color-accent-primary)] bg-[var(--color-bg-base)] p-5">
          <div className="text-center">
            <div className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-3">
              {t('projects.health.modal.formula')}
            </div>
            <div className="font-mono text-base text-[var(--color-text-primary)]">
              {t('projects.health.modal.totalScore')} ={' '}
              <span className="inline-flex items-baseline gap-1">
                <span className="font-bold text-[#3b82f6]">
                  {t('projects.health.modal.progress')}
                </span>
                <span className="text-[var(--color-text-muted)]">×</span>
                <span className="text-lg font-black">40%</span>
              </span>
              <span className="mx-2 text-[var(--color-text-muted)]">+</span>
              <span className="inline-flex items-baseline gap-1">
                <span className="font-bold text-[#8b5cf6]">{t('projects.health.modal.pace')}</span>
                <span className="text-[var(--color-text-muted)]">×</span>
                <span className="text-lg font-black">40%</span>
              </span>
              <span className="mx-2 text-[var(--color-text-muted)]">+</span>
              <span className="inline-flex items-baseline gap-1">
                <span className="font-bold text-[#06b6d4]">
                  {t('projects.health.modal.bufferLabel')}
                </span>
                <span className="text-[var(--color-text-muted)]">×</span>
                <span className="text-lg font-black">20%</span>
              </span>
            </div>
          </div>
        </div>

        {/* 3軸の説明 — 横3列 */}
        <div>
          <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-3">
            {t('projects.health.modal.axes')}
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {/* 進捗 */}
            <div className="rounded-lg border border-[var(--color-border)] p-4">
              <div className="mb-2">
                <span className="text-sm font-bold text-[#3b82f6]">
                  {t('projects.health.modal.progressScore')}
                </span>
                <span className="text-xs text-[var(--color-text-muted)] ml-1">(40%)</span>
              </div>
              <div className="text-[11px] text-[var(--color-text-muted)] mb-3">
                {t('projects.health.modal.spiFormula')}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[var(--color-text-muted)]">
                    <th className="text-left pb-1 font-medium">SPI</th>
                    <th className="text-right pb-1 font-medium">
                      {t('projects.health.modal.score')}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[var(--color-text-secondary)]">
                  <tr>
                    <td>≥ 0.9</td>
                    <td className="text-right font-mono">100</td>
                  </tr>
                  <tr>
                    <td>0.8</td>
                    <td className="text-right font-mono">80</td>
                  </tr>
                  <tr>
                    <td>0.6</td>
                    <td className="text-right font-mono">60</td>
                  </tr>
                  <tr>
                    <td>0.4</td>
                    <td className="text-right font-mono">25</td>
                  </tr>
                  <tr>
                    <td>0</td>
                    <td className="text-right font-mono">0</td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-2 pt-2 border-t border-[var(--color-border)] text-[10px] text-[var(--color-text-muted)]">
                {t('projects.health.modal.spiExample')}
              </div>
            </div>

            {/* ペース */}
            <div className="rounded-lg border border-[var(--color-border)] p-4">
              <div className="mb-2">
                <span className="text-sm font-bold text-[#8b5cf6]">
                  {t('projects.health.modal.paceScore')}
                </span>
                <span className="text-xs text-[var(--color-text-muted)] ml-1">(40%)</span>
              </div>
              <div className="text-[11px] text-[var(--color-text-muted)] mb-3">
                {t('projects.health.modal.paceFormula')}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[var(--color-text-muted)]">
                    <th className="text-left pb-1 font-medium">{t('projects.health.paceRatio')}</th>
                    <th className="text-right pb-1 font-medium">
                      {t('projects.health.modal.score')}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[var(--color-text-secondary)]">
                  <tr>
                    <td>≥ 1.0</td>
                    <td className="text-right font-mono">100</td>
                  </tr>
                  <tr>
                    <td>0.9</td>
                    <td className="text-right font-mono">80</td>
                  </tr>
                  <tr>
                    <td>0.8</td>
                    <td className="text-right font-mono">65</td>
                  </tr>
                  <tr>
                    <td>0.7</td>
                    <td className="text-right font-mono">40</td>
                  </tr>
                  <tr>
                    <td>0</td>
                    <td className="text-right font-mono">10</td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-2 pt-2 border-t border-[var(--color-border)] text-[10px] text-[var(--color-text-muted)]">
                {t('projects.health.modal.paceExample')}
              </div>
            </div>

            {/* バッファ */}
            <div className="rounded-lg border border-[var(--color-border)] p-4">
              <div className="mb-2">
                <span className="text-sm font-bold text-[#06b6d4]">
                  {t('projects.health.modal.bufferScore')}
                </span>
                <span className="text-xs text-[var(--color-text-muted)] ml-1">(20%)</span>
              </div>
              <div className="text-[11px] text-[var(--color-text-muted)] mb-3">
                {t('projects.health.modal.bufferFormula')}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[var(--color-text-muted)]">
                    <th className="text-left pb-1 font-medium">
                      {t('projects.health.modal.bufferRate')}
                    </th>
                    <th className="text-right pb-1 font-medium">
                      {t('projects.health.modal.score')}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[var(--color-text-secondary)]">
                  <tr>
                    <td>≥ 40%</td>
                    <td className="text-right font-mono">100</td>
                  </tr>
                  <tr>
                    <td>20%</td>
                    <td className="text-right font-mono">80</td>
                  </tr>
                  <tr>
                    <td>10%</td>
                    <td className="text-right font-mono">50</td>
                  </tr>
                  <tr>
                    <td>5%</td>
                    <td className="text-right font-mono">20</td>
                  </tr>
                  <tr>
                    <td>0%</td>
                    <td className="text-right font-mono">0</td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-2 pt-2 border-t border-[var(--color-border)] text-[10px] text-[var(--color-text-muted)]">
                {t('projects.health.modal.bufferExample')}
              </div>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
            {t('projects.health.modal.interpolationNote')}
          </p>
        </div>

        {/* 等級 — 横1行 */}
        <div>
          <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
            {t('projects.health.modal.grades')}
          </h3>
          <div className="grid grid-cols-5 gap-2">
            {GRADE_THRESHOLDS.map(([threshold, grade], i) => {
              const nextThreshold = GRADE_THRESHOLDS[i - 1]?.[0] ?? 100;
              const cfg = HEALTH_COLORS[grade];
              return (
                <div
                  key={grade}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 border border-[var(--color-border)]"
                >
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0"
                    style={{ backgroundColor: cfg.bg, color: cfg.text }}
                  >
                    {grade}
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-[var(--color-text-primary)]">
                      {t(cfg.labelKey)}
                    </div>
                    <div className="text-[10px] text-[var(--color-text-muted)]">
                      {String(threshold)}〜{String(nextThreshold)}
                      {t('projects.health.unit')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* データソース */}
        <div>
          <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
            {t('projects.health.modal.dataSource')}
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-[var(--color-text-muted)]">
            <p>
              <strong className="text-[var(--color-text-secondary)]">
                {t('projects.health.modal.budgetPt')}
              </strong>{' '}
              = {t('projects.health.modal.budgetPtDesc')}
            </p>
            <p>
              <strong className="text-[var(--color-text-secondary)]">
                {t('projects.health.modal.startDate')}
              </strong>{' '}
              = {t('projects.health.modal.startDateDesc')}
            </p>
            <p>
              <strong className="text-[var(--color-text-secondary)]">
                {t('projects.health.modal.dueDate')}
              </strong>{' '}
              = {t('projects.health.modal.dueDateDesc')}
            </p>
            <p>
              <strong className="text-[var(--color-text-secondary)]">
                {t('projects.health.modal.integrated')}
              </strong>{' '}
              {t('projects.health.modal.integratedDesc')}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
